import { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { fetchData, deleteData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { useSessionValidation } from '../../../utils/sessionValidation';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Fade, Tooltip, TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EventItemEnquiryModalSingle from './EventItemEnquiryModalSingle';
import EstimateModal from '../Estimate/EstimateModal';
import Pagination from '../../common/Pagination';

function EventItemEnquiryIndex() {
  useSessionValidation();

  const [data, setData] = useState([]);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { isAuthenticated, companyId, branchId, logout, token } = useAuth();

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedItem, setSelectedItem] = useState(null);
  const [fetchingEnquiry, setFetchingEnquiry] = useState(false);

  const [estimateModalOpen, setEstimateModalOpen] = useState(false);
  const [estimateDataFromEnquiry, setEstimateDataFromEnquiry] = useState(null);
  const [loadingEstimateData, setLoadingEstimateData] = useState(false);

  const [categories, setCategories] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [fromDate, setFromDate] = useState(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    return firstDay.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(() => {
    const now = new Date();
    return now.toISOString().split('T')[0];
  });

  useEffect(() => {
    setMounted(true);
    if (isAuthenticated && tokenIsExpired(token)) {
      logout();
      return;
    }
  }, [isAuthenticated, companyId]);

  useEffect(() => {
    if (loading || !companyId || !token) return;

    setLoading(true);
    Promise.all([
      fetchTableData(),
      fetchCategoryData(),
      fetchCustomerData(),
      fetchItemData()
    ]).finally(() => {
      setLoading(false);
    });
  }, [companyId, token]);

  useEffect(() => {
    if (companyId && token && !loading) {
      fetchTableData();
    }
  }, [fromDate, toDate]);

  const fetchTableData = async () => {
    try {
      const response = await fetchData('EnquiryApi', `/EnquriyList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
      console.log('Enquiry data response:', response);

      if (response && response.responseCode === '1111' && response.response_data) {
        setData(response.response_data);
        console.log('Enquiry data loaded successfully');
      } else {
        console.log('Enquiry API failed or returned no data');
        setData([]);
      }
    } catch (error) {
      console.error('Error fetching Enquiry Details:', error);
      setData([]);
    }
  };

  const fetchCategoryData = async () => {
    try {
      const categoryResponse = await fetchData('CategoryApi', `/${companyId}`, token);
      if (categoryResponse && categoryResponse.responseCode === '1111' && categoryResponse.response_data) {
        setCategories(categoryResponse.response_data);
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    }
  };

  const fetchCustomerData = async () => {
    try {
      const customerResponse = await fetchData('CustomerApi', `/${companyId}`, token);
      if (customerResponse && customerResponse.responseCode === '1111' && customerResponse.response_data) {
        setCustomers(customerResponse.response_data);
      } else {
        setCustomers([]);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
      setCustomers([]);
    }
  };

  const fetchItemData = async () => {
    try {
      const itemResponse = await fetchData('ItemApi', `/${companyId}`, token);
      if (itemResponse && itemResponse.responseCode === '1111' && itemResponse.response_data) {
        setItems(itemResponse.response_data);
      } else {
        setItems([]);
      }
    } catch (error) {
      console.error('Error fetching items:', error);
      setItems([]);
    }
  };

  const handleDelete = async () => {
    deleteData('EnquiryApi', `${deleteItemId}`, token)
      .then(() => {
        fetchTableData();
        setDeleteItemId(null);
        setConfirmDialogOpen(false);
      })
      .catch((error) => {
        console.error('Error deleting record:', error);
      });
  };

  const openConfirmDialog = (itemId) => {
    setDeleteItemId(itemId);
    setConfirmDialogOpen(true);
  };

  const closeConfirmDialog = () => {
    setDeleteItemId(null);
    setConfirmDialogOpen(false);
  };

  const fetchSingleEnquiry = async (enquiryId) => {
    setFetchingEnquiry(true);
    try {
      const response = await fetchData('EnquiryApi', `/${enquiryId}`, token);
      let enquiryData = response.response_data || response;

      if (enquiryData.Enquiry && enquiryData.EnquiryDetails) {
        return {
          ...enquiryData.Enquiry,
          EnquiryDetails: enquiryData.EnquiryDetails
        };
      }

      return enquiryData;
    } catch (error) {
      console.error('Error fetching single Enquiry:', error);
      return null;
    } finally {
      setFetchingEnquiry(false);
    }
  };

  const handleOpenModal = (mode, enquiry = null) => {
    setModalMode(mode);
    setSelectedItem(enquiry);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedItem(null);
  };

  const handleSaveEnquiry = () => {
    fetchTableData();
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const filteredData = data.filter((item) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      String(item.EnquiryNo || '').toLowerCase().includes(searchLower) ||
      String(item.EnquiryNumber || '').toLowerCase().includes(searchLower) ||
      String(item.CustomerName || '').toLowerCase().includes(searchLower) ||
      String(item.ContactNumber1 || '').toLowerCase().includes(searchLower) ||
      String(item.Location || '').toLowerCase().includes(searchLower)
    );
  });

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  const columns = [
    { field: 'EnquiryNo', header: 'Enquiry' },
    { field: 'BillDate', header: 'Bill Date' },
    { field: 'CustomerName', header: 'Customer' },
    { field: 'ContactNumber1', header: 'Contact No' },
    { field: 'Location', header: 'Location' },
  ];

  const handleConvertToEstimateClick = async (enquiryId) => {
    setLoadingEstimateData(true);
    try {
      const response = await fetchData('EstimateApi', `/EstimateFromEnquiry/${companyId}/${enquiryId}`, token);

      if (response && response.responseCode === '1111' && response.response_data) {
        const data = response.response_data;

        const estimateData = {
          ...data.Estimate,
          EstimateDetails: data.EstimateDetails?.map(item => ({
            ItemId: item.ItemId,
            ItemName: item.ItemName,
            HSNCode: item.HSNCode,
            Quantity: item.Quantity,
            Rate: item.Rate,
            GSTRate: item.GSTRate,
            EnquiryId: item.EnquiryId,
            EventDays: data.Estimate.EventDays || 1,
            DiscountAmount: 0,
            TaxPercentage: item.GSTRate || 0
          })) || [],
          items: data.EstimateDetails || []
        };

        setEstimateDataFromEnquiry(estimateData);
        setEstimateModalOpen(true);
      } else {
        alert('Error: Could not load estimate data from enquiry');
      }
    } catch (error) {
      console.error('Error fetching enquiry for estimate:', error);
      alert('Error loading enquiry data. Please try again.');
    } finally {
      setLoadingEstimateData(false);
    }
  };

  const renderActions = (item) => (
    <div style={{ display: 'flex', gap: '2px', alignItems: 'center', justifyContent: 'flex-end' }}>
      <Tooltip title="View Details" arrow>
        <Button
          size="small"
          onClick={async () => {
            const fullData = await fetchSingleEnquiry(item.Id);
            handleOpenModal('view', fullData);
          }}
          style={{
            minWidth: '22px',
            height: '22px',
            borderRadius: '6px',
            color: 'white',
            padding: '2px',
            boxShadow: '0 1px 4px rgba(102, 126, 234, 0.3)',
            border: 'none',
            fontSize: '10px',
            fontWeight: '600'
          }}
        >
          👁️
        </Button>
      </Tooltip>

      <Tooltip title="Edit Enquiry" arrow>
        <Button
          size="small"
          onClick={async () => {
            const fullData = await fetchSingleEnquiry(item.Id);
            handleOpenModal('edit', fullData);
          }}
          style={{
            minWidth: '22px',
            height: '22px',
            borderRadius: '6px',
            color: 'white',
            padding: '2px',
            boxShadow: '0 1px 4px rgba(17, 153, 142, 0.3)',
            border: 'none',
            fontSize: '10px',
            fontWeight: '600'
          }}
        >
          ✏️
        </Button>
      </Tooltip>

      <Tooltip title="To Estimate" arrow>
        <Button
          size="small"
          onClick={() => handleConvertToEstimateClick(item.Id)}
          disabled={loadingEstimateData}
          style={{
            minWidth: '22px',
            height: '22px',
            borderRadius: '6px',
            color: 'white',
            padding: '2px',
            boxShadow: '0 1px 4px rgba(139, 92, 246, 0.3)',
            border: 'none',
            fontSize: '10px',
            fontWeight: '600'
          }}
        >
          📊
        </Button>
      </Tooltip>

      <Tooltip title="Delete Enquiry" arrow>
        <Button
          size="small"
          onClick={() => openConfirmDialog(item.Id)}
          style={{
            minWidth: '22px',
            height: '22px',
            borderRadius: '6px',
            color: 'white',
            padding: '2px',
            boxShadow: '0 1px 4px rgba(255, 107, 107, 0.3)',
            border: 'none',
            fontSize: '10px',
            fontWeight: '600'
          }}
        >
          🗑️
        </Button>
      </Tooltip>
    </div>
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  return (
    <Fade in={mounted} timeout={800}>
      <div style={{
        minWidth: '100%',
        background: '#f5f7fa',
        padding: '10px',
        position: 'relative'
      }}>
        <style>{`
          @keyframes slideIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .action-button {
            text-transform: none;
            font-weight: 600;
            letter-spacing: 0.5px;
          }
          .table-row {
            /* No hover animations */
          }
        `}</style>

        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          padding: '12px',
          position: 'relative',
          color: 'black',
          fontFamily: 'Times New Romans',
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '10px',
            paddingBottom: '8px',
            borderBottom: '1px solid #f0f0f0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div>
                <h4 style={{
                  fontSize: '17px',
                  fontWeight: '700',
                  margin: 0,
                  letterSpacing: '-0.3px',
                  fontFamily: 'Times New Romans',
                  textTransform: 'uppercase'
                }}>
                  <span style={{ color: 'black', fontFamily: 'Times New Romans', }}>Rental Enquiry</span>
                </h4>

              </div>
            </div>
          </div>

          {/* Date Filters, Search and Create Button Row - Horizontally Scrollable */}
          <div style={{
            marginBottom: '12px',
            overflowX: 'auto',
            overflowY: 'hidden',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'thin'
          }}>
            <div style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'flex-end',
              minWidth: 'fit-content',
              paddingBottom: '4px'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '120px' : '140px' }}>
                <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>From Date</label>
                <TextField
                  type="date"
                  size="small"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  sx={{
                    width: isMobile ? '120px' : '140px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      fontSize: '11px',
                      height: '32px',
                      '&:hover fieldset': { borderColor: '#667eea' },
                      '&.Mui-focused fieldset': { borderColor: '#667eea' },
                    },
                  }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '120px' : '140px' }}>
                <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>To Date</label>
                <TextField
                  type="date"
                  size="small"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  sx={{
                    width: isMobile ? '120px' : '140px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      fontSize: '11px',
                      height: '32px',
                      '&:hover fieldset': { borderColor: '#667eea' },
                      '&.Mui-focused fieldset': { borderColor: '#667eea' },
                    },
                  }}
                />
              </div>

              {/* Search - Icon on mobile, always visible on desktop */}
              {isMobile ? (
                searchExpanded ? (
                  <TextField
                    size="small"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    autoFocus
                    onBlur={() => {
                      if (!searchTerm) {
                        setSearchExpanded(false);
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon style={{ color: '#667eea', fontSize: '18px' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      minWidth: '250px',
                      width: '350px',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                        backgroundColor: '#f8f9fa',
                        fontSize: '12px',
                        height: '32px',
                        '&:hover fieldset': {
                          borderColor: '#667eea',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#667eea',
                        },
                      },
                    }}
                  />
                ) : (
                  <Tooltip title="Search" arrow>
                    <Button
                      onClick={() => setSearchExpanded(true)}
                      style={{
                        minWidth: '32px',
                        width: '32px',
                        height: '32px',
                        padding: '0',
                        borderRadius: '6px',
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #e2e8f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <SearchIcon style={{ color: '#667eea', fontSize: '18px' }} />
                    </Button>
                  </Tooltip>
                )
              ) : (
                <TextField
                  size="small"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon style={{ color: '#667eea', fontSize: '18px' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    flex: 1,
                    minWidth: '250px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      fontSize: '12px',
                      height: '32px',
                      '&:hover fieldset': {
                        borderColor: '#667eea',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea',
                      },
                    },
                  }}
                />
              )}

              <Tooltip title="Create a new enquiry" arrow>
                <Button
                  variant="contained"
                  onClick={() => handleOpenModal('create')}
                  className="action-button"
                  style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    padding: isMobile ? '0' : '4px 12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '700',
                    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.4)',
                    border: 'none',
                    height: '32px',
                    textTransform: 'none',
                    letterSpacing: '0.5px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                    whiteSpace: 'nowrap',
                    minWidth: isMobile ? '32px' : '100px',
                    width: 'auto',
                    marginLeft: 'auto'
                  }}
                >
                  <AddIcon style={{ fontSize: '18px' }} />
                  {!isMobile && <span style={{ fontSize: '12px' }}>Create</span>}
                </Button>
              </Tooltip>
            </div>
          </div>

          <div style={{ marginBottom: '10px' }}>
            <TableContainer component={Paper} style={{
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
              borderRadius: '10px',
              overflow: 'hidden'
            }}>
              <Table sx={{ minWidth: 650 }} aria-label="enquiry table">
                <TableHead>
                  <TableRow style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                  }}>
                    {columns.map((column) => (
                      <TableCell key={column.field} style={{
                        color: 'white',
                        fontWeight: '600',
                        fontSize: isMobile ? '10px' : '11px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        padding: isMobile ? '6px 8px' : '4px 8px',
                        textAlign: 'left',
                        fontSize: '10px',
                      }}>
                        {column.header}
                      </TableCell>
                    ))}
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: isMobile ? '10px' : '11px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      padding: isMobile ? '6px 8px' : '8px 12px',
                      textAlign: 'right',
                      padding: '4px 8px',
                      fontSize: '10px',
                    }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.map((item, index) => (
                    <TableRow key={item.Id} className="table-row" style={{
                      background: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                      borderBottom: '2px solid #f0f0f0'
                    }}>
                      <TableCell style={{ fontSize: isMobile ? '10px' : '12px', padding: isMobile ? '6px 8px' : '8px 12px', color: '#2d3748' }}>
                        {item.EnquiryNo || `ENQ-${String(item.EnquiryNumber || item.Id).padStart(4, '0')}`}
                      </TableCell>
                      <TableCell style={{ fontSize: isMobile ? '10px' : '12px', padding: isMobile ? '6px 8px' : '8px 12px', color: '#2d3748' }}>
                        {formatDate(item.BillDate)}
                      </TableCell>
                      <TableCell style={{ fontSize: isMobile ? '10px' : '12px', padding: isMobile ? '6px 8px' : '8px 12px', color: '#2d3748' }}>
                        {item.CustomerName || 'N/A'}
                      </TableCell>
                      <TableCell style={{ fontSize: isMobile ? '10px' : '12px', padding: isMobile ? '6px 8px' : '8px 12px', color: '#2d3748' }}>
                        {item.ContactNumber1 || 'N/A'}
                      </TableCell>
                      <TableCell style={{ fontSize: isMobile ? '10px' : '12px', padding: isMobile ? '6px 8px' : '8px 12px', color: '#2d3748' }}>
                        {item.Location || 'N/A'}
                      </TableCell>
                      <TableCell style={{ padding: isMobile ? '6px 8px' : '8px 12px', textAlign: 'right' }}>
                        {renderActions(item)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            pageSizeOptions={[10, 25, 50, 100]}
          />
        </div>

        <Dialog
          open={confirmDialogOpen}
          onClose={closeConfirmDialog}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{
            style: {
              borderRadius: '12px',
              padding: '8px'
            }
          }}
        >
          <DialogTitle id="alert-dialog-title" style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#2d3748'
          }}>
            Delete Record?
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description" style={{
              fontSize: '13px',
              color: '#666'
            }}>
              Are you sure you want to delete this enquiry record?
            </DialogContentText>
          </DialogContent>
          <DialogActions style={{ padding: '8px 12px', gap: '6px' }}>
            <Button
              onClick={closeConfirmDialog}
              style={{
                background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                color: '#ffffff',
                borderRadius: '6px',
                padding: '4px 8px',
                fontSize: '12px',
                fontWeight: '600',
                textTransform: 'none',
                boxShadow: '0 1px 4px rgba(255, 107, 107, 0.3)',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                minHeight: '26px'
              }}
            >
              <span style={{ fontSize: '12px' }}>❌</span>
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              style={{
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                color: '#ffffff',
                borderRadius: '6px',
                padding: '4px 8px',
                fontSize: '12px',
                fontWeight: '600',
                textTransform: 'none',
                boxShadow: '0 1px 4px rgba(17, 153, 142, 0.3)',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                minHeight: '26px'
              }}
            >
              <span style={{ fontSize: '12px' }}>✅</span>
              Confirm Delete
            </Button>
          </DialogActions>
        </Dialog>

        <EventItemEnquiryModalSingle
          open={modalOpen}
          onClose={handleCloseModal}
          onSave={handleSaveEnquiry}
          enquiryData={selectedItem}
          mode={modalMode}
          categories={categories}
          customers={customers}
          items={items}
        />

        {estimateModalOpen && (
          <EstimateModal
            open={estimateModalOpen}
            onClose={() => {
              setEstimateModalOpen(false);
              setEstimateDataFromEnquiry(null);
            }}
            onSave={() => {
              setEstimateModalOpen(false);
              setEstimateDataFromEnquiry(null);
              fetchTableData();
            }}
            estimateData={estimateDataFromEnquiry}
            mode="create"
            customers={customers}
            items={items}
          />
        )}

      </div>
    </Fade>
  );
}

export default EventItemEnquiryIndex;
